import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../shared/abstractStore';
import { profileQuery, profileQueryVariables } from '../generated/profileQuery';
import { profileFieldsFragment } from '../models/Profile';
import { mainApiConfig } from '../../../configure/mainApiConfig';

export interface ProfileOptions extends AsyncLoadOptions {
    isUserId?: boolean,
}

/**
 * Get a Profile from the store.
 * @param id
 * @param options
 */
export function useProfile(id: string | undefined | null, options: ProfileOptions = {}): AsyncLoadResult<profileQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<profileQuery, profileQueryVariables>(
        gql`
        query profileQuery ($id: ID, $userId: ID) {
            model: profile(id: $id, userId: $userId) {
                ...profileFields
            }
        }

        ${profileFieldsFragment}
        `,
        {
            variables: {
                id: !options.isUserId ? id ?? Guid.empty : null,
                userId: options.isUserId ? id ?? Guid.empty : null
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
